import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type {
  TrainingModuleCategory,
  TrainingModuleDetail,
  TrainingTopic,
  TrainingTopicPayload,
} from "@xop/dashboard-shared";

import { useCan } from "../../../auth/permissions.js";
import { PageHeader } from "../../../components/PageHeader.js";
import { BackButton } from "../../../components/ui/back-button.js";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog.js";
import { toast } from "../../../components/ui/use-toast.js";
import {
  TRAINING_LIST_KEY,
  createTrainingTopic,
  deleteTrainingModule,
  deleteTrainingTopic,
  getTrainingModule,
  removeTrainingVideo,
  reorderTrainingTopics,
  trainingErrorMessage,
  trainingModuleKey,
  updateTrainingModule,
  updateTrainingTopic,
  uploadTrainingVideo,
} from "./api.js";
import { PublishedChip } from "./ModuleCategory.js";
import { TopicMenuEditor } from "./TopicMenuEditor.js";
import { TopicPanel } from "./TopicPanel.js";
import { CATEGORY_LABEL, findTopic, moduleStats, moveTopic, reorderRows } from "./trainingLogic.js";

/**
 * Documentation ▸ Online Training ▸ one module.
 *
 * Three things on one page, top to bottom of importance for an author:
 *   1. the module itself — title, kind, summary, Published — saved with Save;
 *   2. its TOPIC MENU on the left — sections and the lessons under them, the
 *      same two levels the reseller's player shows beside the video;
 *   3. the selected topic on the right — its video (link or upload, with a
 *      preview) and its words.
 *
 * Every write answers with the WHOLE module (portal-api's contract), so the
 * page never patches its copy by hand: it drops the answer into the query
 * cache and re-renders. Moves send the whole menu's order in one request
 * (`reorderRows`) so a half-applied move can never be what a reseller sees.
 */
export function OnlineTrainingEditorPage(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const canEdit = useCan("documentation:edit");
  const qc = useQueryClient();
  const navigate = useNavigate();

  const key = trainingModuleKey(id);
  const { data: mod, isLoading, error } = useQuery({
    queryKey: key,
    queryFn: () => getTrainingModule(id),
    enabled: id !== "",
  });

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ topic: TrainingTopic; lessons: number } | null>(null);
  const [deletingModule, setDeletingModule] = useState(false);

  const apply = (next: TrainingModuleDetail): void => {
    qc.setQueryData(key, next);
    void qc.invalidateQueries({ queryKey: TRAINING_LIST_KEY });
  };

  /** Run a write; on success replace the module, on failure say why and rethrow
   *  so the inline editor that asked keeps its state. `failTitle: null` = the
   *  caller shows the error inline itself (the video box), so no toast too. */
  const write = async (fn: () => Promise<TrainingModuleDetail>, failTitle: string | null): Promise<TrainingModuleDetail> => {
    setBusy(true);
    try {
      const next = await fn();
      apply(next);
      return next;
    } catch (err) {
      if (failTitle) toast({ title: failTitle, description: trainingErrorMessage(err), variant: "destructive" });
      throw err;
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "grid", gap: 2 }}>
        <Skeleton variant="rounded" sx={{ height: 40, width: 120 }} />
        <Skeleton variant="rounded" sx={{ height: 180 }} />
        <Skeleton variant="rounded" sx={{ height: 360 }} />
      </Box>
    );
  }

  if (error || !mod) {
    return (
      <Box sx={{ display: "grid", gap: 2 }}>
        <Box>
          <BackButton to="/documentation/online-training" label="Online Training" />
        </Box>
        <Alert severity="error">{error ? trainingErrorMessage(error) : "This module was not found."}</Alert>
      </Box>
    );
  }

  const selected = selectedId != null ? findTopic(mod.sections, selectedId) : null;

  return (
    <Box data-testid="training-editor-page" sx={{ display: "grid", gap: 2.5 }}>
      <Box>
        <BackButton to="/documentation/online-training" label="Online Training" />
      </Box>

      <PageHeader
        dense
        title={mod.title}
        subtitle={`${CATEGORY_LABEL[mod.category]} · ${moduleStats(mod)}`}
        right={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PublishedChip published={mod.published} />
            {canEdit && (
              <Button
                color="error"
                variant="outlined"
                startIcon={<Trash2 size={16} />}
                onClick={() => setDeletingModule(true)}
                data-testid="training-delete-module"
              >
                Delete module
              </Button>
            )}
          </Box>
        }
      />

      <ModuleForm
        // Remount when the saved module changes so the form shows what was saved.
        key={`${mod.id}:${mod.updated_at}`}
        module={mod}
        canEdit={canEdit}
        onSave={async (payload) => {
          await write(() => updateTrainingModule(mod.id, payload), "Couldn't save the module");
          toast({ title: "Module saved" });
        }}
      />

      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", lg: "minmax(300px, 380px) minmax(0, 1fr)" },
          alignItems: "start",
        }}
      >
        <Card variant="outlined" sx={{ position: { lg: "sticky" }, top: { lg: 16 } }}>
          <CardContent sx={{ display: "grid", gap: 1 }}>
            <Box>
              <Typography variant="h6" component="h2" sx={{ fontSize: 15, fontWeight: 600 }}>
                Topic menu
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Shown beside the video, in this order. Sections hold lessons; each is one short video.
              </Typography>
            </Box>
            <TopicMenuEditor
              sections={mod.sections}
              selectedId={selectedId}
              canEdit={canEdit}
              busy={busy}
              actions={{
                onSelect: setSelectedId,
                onAddSection: async (title) => {
                  const next = await write(() => createTrainingTopic(mod.id, { title, parent_id: null }), "Couldn't add the section");
                  const added = next.sections.find((s) => !mod.sections.some((o) => o.id === s.id));
                  if (added) setSelectedId(added.id);
                },
                onAddLesson: async (sectionId, title) => {
                  const next = await write(() => createTrainingTopic(mod.id, { title, parent_id: sectionId }), "Couldn't add the lesson");
                  const before = new Set(mod.sections.flatMap((s) => s.lessons.map((l) => l.id)));
                  const added = next.sections.find((s) => s.id === sectionId)?.lessons.find((l) => !before.has(l.id));
                  if (added) setSelectedId(added.id);
                },
                onRename: async (topicId, title) => {
                  await write(() => updateTrainingTopic(topicId, { title }), "Couldn't rename the topic");
                },
                onMove: (topicId, dir) => {
                  const moved = moveTopic(mod.sections, topicId, dir);
                  if (!moved) return;
                  // Show the move at once; the server's answer then replaces it
                  // (and a failure puts the old order back).
                  qc.setQueryData(key, { ...mod, sections: moved });
                  write(() => reorderTrainingTopics(mod.id, reorderRows(moved)), "Couldn't move the topic").catch(() => {
                    qc.setQueryData(key, mod);
                  });
                },
                onDelete: (topic, lessons) => setPendingDelete({ topic, lessons }),
              }}
            />
          </CardContent>
        </Card>

        <Box sx={{ minWidth: 0 }}>
          {selected ? (
            <TopicPanel
              key={`${selected.id}:${selected.title}:${selected.summary ?? ""}:${selected.duration_seconds ?? ""}`}
              topic={selected}
              isSection={selected.parent_id == null}
              canEdit={canEdit}
              actions={{
                onSave: async (payload: TrainingTopicPayload) => {
                  // A link save reports inline in the video box; the words toast.
                  const inline = payload.video_url !== undefined;
                  await write(() => updateTrainingTopic(selected.id, payload), inline ? null : "Couldn't save the topic");
                },
                onUpload: async (file) => {
                  await write(() => uploadTrainingVideo(selected.id, file), null);
                },
                onRemoveVideo: async () => {
                  await write(
                    () =>
                      selected.video?.kind === "file"
                        ? removeTrainingVideo(selected.id)
                        : updateTrainingTopic(selected.id, { video_url: "" }),
                    "Couldn't remove the video",
                  );
                },
              }}
            />
          ) : (
            <Card variant="outlined" sx={{ borderStyle: "dashed" }}>
              <CardContent sx={{ py: 6, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {mod.sections.length === 0
                    ? canEdit
                      ? "Start the menu on the left with a section."
                      : "This module has no topics yet."
                    : "Pick a topic on the left to see its video."}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>

      <ConfirmDialog
        isOpen={pendingDelete != null}
        onClose={() => setPendingDelete(null)}
        title={pendingDelete?.topic.parent_id == null ? "Delete this section?" : "Delete this lesson?"}
        description={
          pendingDelete
            ? pendingDelete.topic.parent_id == null && pendingDelete.lessons > 0
              ? `“${pendingDelete.topic.title}” and the ${pendingDelete.lessons} ${pendingDelete.lessons === 1 ? "lesson" : "lessons"} under it are deleted, with their videos.`
              : `“${pendingDelete.topic.title}” is deleted, with its video.`
            : undefined
        }
        confirmLabel="Delete"
        onConfirm={async () => {
          if (!pendingDelete) return;
          const doomed = pendingDelete.topic.id;
          await write(() => deleteTrainingTopic(doomed), "Couldn't delete the topic");
          if (selectedId != null && (selectedId === doomed || findTopic(mod.sections, selectedId)?.parent_id === doomed)) {
            setSelectedId(null);
          }
        }}
      />

      <ConfirmDialog
        isOpen={deletingModule}
        onClose={() => setDeletingModule(false)}
        title="Delete this module?"
        description={`“${mod.title}” is deleted with its whole topic menu and every uploaded video. Resellers lose it at once.`}
        confirmLabel="Delete module"
        onConfirm={async () => {
          try {
            await deleteTrainingModule(mod.id);
          } catch (err) {
            toast({ title: "Couldn't delete the module", description: trainingErrorMessage(err), variant: "destructive" });
            throw err;
          }
          qc.removeQueries({ queryKey: key });
          void qc.invalidateQueries({ queryKey: TRAINING_LIST_KEY });
          navigate("/documentation/online-training");
        }}
      />
    </Box>
  );
}

function ModuleForm({
  module: m,
  canEdit,
  onSave,
}: {
  module: TrainingModuleDetail;
  canEdit: boolean;
  onSave: (payload: { title: string; summary: string | null; category: TrainingModuleCategory; published: boolean }) => Promise<void>;
}): JSX.Element {
  const [title, setTitle] = useState(m.title);
  const [summary, setSummary] = useState(m.summary ?? "");
  const [category, setCategory] = useState<TrainingModuleCategory>(m.category);
  const [published, setPublished] = useState(m.published);
  const [saving, setSaving] = useState(false);

  const dirty =
    title.trim() !== m.title ||
    (summary.trim() || null) !== (m.summary ?? null) ||
    category !== m.category ||
    published !== m.published;

  const save = async (): Promise<void> => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onSave({ title: title.trim(), summary: summary.trim() || null, category, published });
    } catch {
      // reported by the page
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" data-testid="training-module-form">
      <CardContent>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 2fr) minmax(160px, 1fr)" },
          }}
        >
          <TextField
            label="Title"
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={!canEdit}
            required
            inputProps={{ "data-testid": "module-title-input" }}
          />
          <TextField
            select
            label="Kind"
            size="small"
            value={category}
            onChange={(e) => setCategory(e.target.value as TrainingModuleCategory)}
            disabled={!canEdit}
            SelectProps={{ native: true }}
            inputProps={{ "data-testid": "module-category-input" }}
          >
            {(Object.keys(CATEGORY_LABEL) as TrainingModuleCategory[]).map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABEL[c]}
              </option>
            ))}
          </TextField>
          <TextField
            label="Summary"
            size="small"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            disabled={!canEdit}
            multiline
            minRows={2}
            sx={{ gridColumn: { md: "1 / -1" } }}
            inputProps={{ "data-testid": "module-summary-input" }}
          />
        </Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mt: 2, flexWrap: "wrap" }}>
          <FormControlLabel
            control={
              <Switch
                checked={published}
                onChange={(e) => setPublished(e.target.checked)}
                disabled={!canEdit}
                inputProps={{ "aria-label": "Published" }}
                data-testid="module-published-switch"
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Published
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {published ? "Resellers can open this module." : "A draft — only staff see it."}
                </Typography>
              </Box>
            }
          />
          {canEdit && (
            <Button
              variant="contained"
              disabled={!dirty || saving || !title.trim()}
              onClick={() => void save()}
              data-testid="module-save"
            >
              {saving ? "Saving…" : "Save module"}
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
